if (!customElements.get('bt-header-language-tabs')) {
  class BtHeaderLanguageTabs extends HTMLElement {
    constructor() {
      super();
      this.track = this.querySelector('.bt-lang-tabs__track');
      this.tabs = [...this.querySelectorAll('.bt-lang-tabs__tab')];
      this.form = this.closest('form');
      this.localeInput = this.form?.querySelector('input[name="locale_code"]');
      this.swipeThreshold = 48;
      this.touchStartX = null;
      this.isTouching = false;

      this.handleTabClick = this.handleTabClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);

      this.tabs.forEach((tab) => tab.addEventListener('click', this.handleTabClick));
      this.track.addEventListener('keydown', this.handleKeydown);
      this.track.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.track.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    }

    disconnectedCallback() {
      this.tabs.forEach((tab) => tab.removeEventListener('click', this.handleTabClick));
      this.track.removeEventListener('keydown', this.handleKeydown);
      this.track.removeEventListener('touchstart', this.handleTouchStart);
      this.track.removeEventListener('touchend', this.handleTouchEnd);
    }

    get activeIndex() {
      return this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    }

    get isRtl() {
      return document.documentElement.getAttribute('dir') === 'rtl';
    }

    handleTabClick(evt) {
      const tab = evt.currentTarget;
      if (tab.getAttribute('aria-selected') === 'true') return;
      this.selectIndex(this.tabs.indexOf(tab), true);
    }

    handleKeydown(evt) {
      const { key } = evt;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) return;

      evt.preventDefault();
      const last = this.tabs.length - 1;
      let next = this.activeIndex;

      if (key === 'ArrowLeft') {
        next = this.isRtl ? Math.min(last, next + 1) : Math.max(0, next - 1);
      }
      if (key === 'ArrowRight') {
        next = this.isRtl ? Math.max(0, next - 1) : Math.min(last, next + 1);
      }
      if (key === 'Home') next = 0;
      if (key === 'End') next = last;

      if (next !== this.activeIndex) {
        this.selectIndex(next, true);
        this.tabs[next].focus();
      }
    }

    handleTouchStart(evt) {
      if (evt.touches.length !== 1) return;
      this.touchStartX = evt.touches[0].clientX;
      this.isTouching = true;
    }

    handleTouchEnd(evt) {
      if (!this.isTouching || this.touchStartX === null) return;
      this.isTouching = false;

      const touchEndX = evt.changedTouches[0]?.clientX;
      if (touchEndX == null) return;

      const delta = touchEndX - this.touchStartX;
      this.touchStartX = null;

      if (Math.abs(delta) < this.swipeThreshold) return;

      const last = this.tabs.length - 1;
      const next = delta < 0
        ? Math.min(last, this.activeIndex + 1)
        : Math.max(0, this.activeIndex - 1);

      if (next !== this.activeIndex) {
        this.selectIndex(next, true);
      }
    }

    selectIndex(index, submit) {
      const tab = this.tabs[index];
      if (!tab) return;

      const locale = tab.dataset.locale;
      const currentLocale = this.localeInput?.value;

      this.tabs.forEach((t, i) => {
        const selected = i === index;
        t.setAttribute('aria-selected', selected ? 'true' : 'false');
        t.tabIndex = selected ? 0 : -1;
      });

      this.style.setProperty('--bt-lang-active', String(index));

      if (!this.localeInput || !locale) return;
      this.localeInput.value = locale;

      if (submit && locale !== currentLocale) {
        this.form.requestSubmit?.() ?? this.form.submit();
      }
    }
  }

  customElements.define('bt-header-language-tabs', BtHeaderLanguageTabs);
}
